import { useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { modalFormSchema, type ModalFormData } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { MediaField } from '../MediaField';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { usePostPostsMutation } from '@/services/queries/posts';
import { PostStatus } from '@/model';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PostsCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const PostsCreateModal = ({ isOpen, onClose, onDone }: PostsCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postMutation = usePostPostsMutation();

  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<ModalFormData>({
    resolver: zodResolver(modalFormSchema),
    defaultValues: {
      identifier: '',
      content: '',
      posts_media: [],
      status: PostStatus.PUBLISHED,
      disable_comments: false,
      enable_home: true,
    },
  });

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
    handleSubmit,
    control,
  } = form;

  const formValues = watch();

  const renameFile = (file: File, newName: string): File => {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const handleCreate = (data: ModalFormData) => {
    const postsMediaMeta: { uuid: string }[] = [];
    const mediaFiles: Record<string, File> = {};

    data.posts_media.forEach(mediaItem => {
      const uuid = uuidv4();
      const fileToSend = mediaItem.file_cropped ?? mediaItem.file;

      postsMediaMeta.push({ uuid });

      const extension = fileToSend.name.split('.').pop();
      const renamed = renameFile(fileToSend, `${uuid}.${extension}`);

      mediaFiles[uuid] = renamed;
    });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      identifier: data.identifier,
      content: data.content,
      status: data.status,
      disable_comments: data.disable_comments,
      enable_home: data.enable_home,
      posts_media: postsMediaMeta,
      ...mediaFiles,
    };

    postMutation.mutate(payload, {
      onSuccess: () => {
        toast({ title: t('pages.application_posts.components.posts_create_modal.posts_create_success'), status: 'success' });
        onDone?.();
        reset();
      },
      onError: (e: any) => {
        toast({
          title: e?.message || t('pages.application_posts.components.posts_create_modal.posts_create_error'),
          status: 'error',
        });
      },
    });
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent as='form' ref={formRef} onSubmit={handleSubmit(handleCreate)}>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_posts.components.posts_create_modal.posts_create_title')}
          </ModalHeader>
          <ModalBody>
            <Flex direction='column' gap={4}>
              <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
                control={control}
                name='team_uuid'
                prefetchOnOpen
                label={t('pages.application_posts.components.posts_create_modal.posts_create_team_label')}
                placeholder={t('pages.application_posts.components.posts_create_modal.posts_create_team_label')}
                showFlag
                width='100%'
                usePortal={false}
                error={errors?.team_uuid?.message as string | undefined}
              />
              <InputField
                withoutFloating
                label={t('pages.application_posts.components.posts_create_modal.posts_create_identifier_label')}
                helperText={t('pages.application_posts.components.posts_create_modal.posts_create_identifier_helper_text')}
                maxLength={20}
                {...register('identifier')}
                rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
              />

              <InputField
                withoutFloating
                label={t('pages.application_posts.components.posts_create_modal.posts_create_content_label')}
                helperText={t('pages.application_posts.components.posts_create_modal.posts_create_content_helper_text')}
                maxLength={500}
                {...register('content')}
                rootProps={{ isInvalid: !!errors?.content, errorMessage: errors?.content?.message }}
              />

              <FormProvider {...form}>
                <MediaField />
              </FormProvider>

              <Switch
                isChecked={!!formValues.disable_comments}
                onChange={event => setValue('disable_comments', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_posts.components.posts_create_modal.posts_create_disable_comments_label')}
              </Switch>

              <Switch
                isChecked={!!formValues.enable_home}
                onChange={event => setValue('enable_home', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_posts.components.posts_create_modal.posts_create_enable_home_label')}
              </Switch>              
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Flex
              w='100%'
              direction={{ base: 'column', md: 'row' }}
              justifyContent={{ md: 'space-between' }}
              alignItems={{ md: 'center' }}
              gap={2}
            >
              <Button
                display='flex'
                alignItems='center'
                gap={2}
                onClick={() => {
                  setValue('status', PostStatus.DRAFT);
                  formRef.current?.requestSubmit();
                }}
                width={{ base: '100%' }}
                p={0}
                color='brand.500'
                backgroundColor='transparent'
                textDecoration='underline'
                _hover={{
                  backgroundColor: 'transparent',
                  color: 'brand.500',
                }}
              >
                <ChakraFeatherIcon name='save' />
                {t('pages.application_posts.components.posts_create_modal.posts_create_button_save_draft')}
              </Button>

              <Flex direction={{ base: 'column', md: 'row' }} w='100%' justifyContent='end' gap={4}>
                <Button variant='outlined' color='gray.600' onClick={handleClose}>
                  {t('pages.application_posts.components.posts_create_modal.posts_create_button_cancel')}
                </Button>
                <Button type='submit' bg='green.500' paddingX={10}>
                  {t('pages.application_posts.components.posts_create_modal.posts_create_button_create')}
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={postMutation.isLoading} />
    </>
  );
};
